import { fetchFileContentUrl } from "@hooks/queries/use-files";

export async function streamToString(readableStream) {
  const reader = readableStream.getReader();
  const decoder = new TextDecoder("utf-8");
  let result = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break; // Stop reading when stream is finished
    result += decoder.decode(value, { stream: true }); // Incremental decoding
  }

  return result;
}

const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/**
 * Get a URL or content for a file.
 *
 * MDRS-503: the bytes come from the authenticated content proxy
 * (`GET /files/{id}/content`) rather than a file-scoped SAS URL — the proxy
 * re-checks ownership on every fetch, where the SAS only checked it at mint
 * time. The text branch still needs the raw bytes, so it re-reads the object
 * URL (a local `blob:` read, no network).
 *
 * Accepts either `file.id` or `file.fileId` (split/uncategorised docs carry
 * their id under `fileId`); only the file owner can obtain access.
 */
export async function getFileUrl(file) {
  const fileId = file.id || file.fileId;
  const fileExtension = file.name.split(".").pop().toLowerCase();

  if (fileExtension === "pdf") {
    return {
      url: await fetchFileContentUrl(fileId, "application/pdf"),
      type: "pdf",
    };
  }
  if (["doc", "docx"].includes(fileExtension)) {
    return { url: await fetchFileContentUrl(fileId, DOCX_MIME_TYPE), type: "doc" };
  }

  const objectUrl = await fetchFileContentUrl(fileId);
  try {
    const res = await fetch(objectUrl);
    const content = await streamToString(res.body);
    return { content, type: "text" };
  } finally {
    // The caller only gets the decoded text, so nothing else holds this URL.
    URL.revokeObjectURL(objectUrl);
  }
}
